import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PARTNER_USER_ROLE_OPTIONS } from 'vopay';

/**
 * Localized labels for the fixed Partner Portal user roles (Admin/Support/Viewer).
 *
 * `PARTNER_USER_ROLE_OPTIONS` is a `vopay` constant whose **keys** are the role values submitted to
 * the API and matched on in logic, and whose values are English display labels. Per the
 * i18n-conventions "config-/API-derived labels that double as logic keys" rule, the keys are kept
 * verbatim and only the rendered string is localized.
 *
 * Unlike Account Portal (client-dashboard), where roles are user-defined/dynamic and shown as-is,
 * Partner Portal roles are a fixed, closed set, so they're translated here rather than passed through.
 */
export default function useUserRoleLabels(): { [role: string]: string } {
  const { t } = useTranslation('translations', { keyPrefix: 'Hooks.UseUserRoleLabels' });

  return useMemo(() => {
    const labels = new Map<string, string>([
      ['Admin', t('admin', { defaultValue: 'Admin' })],
      ['Support', t('support', { defaultValue: 'Support' })],
      ['Viewer', t('viewer', { defaultValue: 'Viewer' })],
    ]);

    return Object.fromEntries(
      Object.entries(PARTNER_USER_ROLE_OPTIONS).map(([role, englishLabel]) => [role, labels.get(role) ?? englishLabel]),
    );
  }, [t]);
}
